import { useState } from 'react';
import { BiPlus } from 'react-icons/bi';

import ButtonSmall from '@/components/Button/ButtonSmall';
import ContextMenu from '@/components/ContextMenu/ContextMenu';
import { Input } from '@/components/Inputs';
import SelectionChip from '@/components/SelectionChip/SelectionChip';
import { MENU_TAG_NAME } from '@/store/tags/tags.dto';
import type { EventTag } from '@/store/tags/tags.mappers';
import { useCreateTagMutation, useEventTagsQuery } from '@/store/tags/tags.store';
import { toast } from '@/utils/toast';

import s from './TagPicker.module.scss';

const NEW_TAG_MAX = 50;

type TagPickerProps = {
  /** The chosen tag ids. */
  value: string[];
  onChange: (tagIds: string[]) => void;
  /** Tags already on the event, for names the vessel list may not have yet. */
  known?: EventTag[];
  canEdit?: boolean;
  disabled?: boolean;
  /** The AI is still picking — a hint in place of the empty text. */
  suggesting?: boolean;
  emptyText?: string;
};

/**
 * An event's tags as chips: remove one, add one of the vessel's tags, or create a
 * new one (a name that is taken — "guests" — just picks the existing tag). Menu
 * belongs to the chef's menus: shown, never offered or removed here.
 */
export const TagPicker = ({
  value,
  onChange,
  known = [],
  canEdit = true,
  disabled = false,
  suggesting = false,
  emptyText = 'No tags yet',
}: TagPickerProps) => {
  const { data: vesselTags = [] } = useEventTagsQuery(canEdit);
  const createTag = useCreateTagMutation();
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const byId = new Map([...known, ...vesselTags].map((tag) => [tag.id, tag]));
  const chosen = value.flatMap((id) => {
    const tag = byId.get(id);
    return tag ? [tag] : [];
  });
  const chosenIds = new Set(value);
  const addable = vesselTags.filter((tag) => !chosenIds.has(tag.id) && tag.name !== MENU_TAG_NAME);
  const busy = disabled || createTag.isPending;

  const closeNew = () => {
    setNewOpen(false);
    setNewName('');
  };

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    try {
      const tag = await createTag.mutateAsync(name);
      if (!chosenIds.has(tag.id)) onChange([...value, tag.id]);
      closeNew();
    } catch (error) {
      console.error('Tag create error:', error);
      toast.error('Couldn’t create the tag. Please try again.');
    }
  };

  return (
    <div className={s.root}>
      <div className={s.chips}>
        {chosen.map((tag) => (
          <SelectionChip
            key={tag.id}
            label={tag.name}
            size="sm"
            onRemove={
              canEdit && !busy && tag.name !== MENU_TAG_NAME
                ? () => onChange(value.filter((id) => id !== tag.id))
                : undefined
            }
          />
        ))}
        {chosen.length === 0 && (
          <span className={s.empty}>{suggesting ? 'Picking tags…' : emptyText}</span>
        )}
        {canEdit && !newOpen && (
          <button
            type="button"
            className={s.addBtn}
            disabled={busy}
            onClick={(e) => setAddAnchor(e.currentTarget)}
          >
            <BiPlus aria-hidden="true" /> Add
          </button>
        )}
      </div>

      {newOpen && (
        <div className={s.newRow}>
          <Input
            autoFocus
            value={newName}
            maxLength={NEW_TAG_MAX}
            placeholder="Engine room"
            aria-label="New tag name"
            className={s.newInput}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submitNew();
              } else if (e.key === 'Escape') {
                e.stopPropagation();
                closeNew();
              }
            }}
          />
          <ButtonSmall onClick={() => void submitNew()} disabled={!newName.trim() || busy}>
            {createTag.isPending ? 'Adding…' : 'Add'}
          </ButtonSmall>
          <button type="button" className={s.cancelBtn} onClick={closeNew}>
            Cancel
          </button>
        </div>
      )}

      <ContextMenu
        isOpen={!!addAnchor}
        onClose={() => setAddAnchor(null)}
        anchorEl={addAnchor}
        items={[
          ...addable.map((tag) => ({
            label: tag.name,
            onClick: () => onChange([...value, tag.id]),
          })),
          {
            label: 'Add Tag',
            icon: <BiPlus aria-hidden="true" />,
            onClick: () => setNewOpen(true),
          },
        ]}
      />
    </div>
  );
};

export default TagPicker;
